import type {ReactElement, TransitionEvent} from 'react';
import {useEffect, useRef, useState} from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {doesAccountSupportMultipleMediaTypes, isFacebookAccount} from '@Components/social-media/account.vo';
import {getAccountMediaTypeSubtitleSuffix} from '@Libraries/social-media-params.library';
import {SocialAccountLabel} from '@Components/social-media/components/social-account-label';
import type {SocialPostPlatformSelectionTransitionEventDetail} from '@Components/social-media/social-media.types';
import {SocialPostCaptionOverwriteEvent, SocialPostPlatformSelectionTransitionEvent} from '@Components/social-media/social-media.types';
import {
  useAccountResizeCompletedStatusForPosters,
  usePostPublishingParamsForAccount,
  usePublishingParams,
  usePublishingStatusForAccountWithMediaType,
  useReelPublishingParamsForAccount,
  useSocialPostPublishingAction,
  useStoryGuidelineForAccount,
  useStoryPublishingParamsForAccount,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import {SocialMediaWizardContentSize, SocialMediaWizardMediaTypeRadioButtonIds, SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import {
  useDesignsGraphicsMetaMapForAccountWithMediaType,
  useDesignsToPublish,
  useDoesAnyPosterRequireResizeForAccount,
  useHasAnyPosterResizeErroredForAccount,
  useIsAnyPosterLoadingForAccount,
  useIsAnyPosterResizingForAccount,
  useIsPublishingVideoForAccount,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {FieldSpacer} from '@Components/social-media-wizard/components/field-spacer';
import {
  setMediaTypeSelectionToPost,
  setMediaTypeSelectionToPostAndStory,
  setMediaTypeSelectionToReel,
  setMediaTypeSelectionToReelAndStory,
  setMediaTypeSelectionToStory,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {usePublishParamsCoverFlow, usePublishParamsResizeActions, usePublishParamsResizePipeline} from './social-media-post-publish-params.hooks';
import {useRemoveAccountFromPost} from '@Components/social-media-wizard/social-media-wizard-feature.hooks';
import {PublishParamsPreviewCta} from './components/publish-params-preview-cta/publish-params-preview-cta';
import {PublishingPost} from '@Components/social-media-wizard/components/publishing-post';
import {LoadingStates} from '@Utils/loading.util';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {SocialPostContentType} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {SocialPostContent} from '@Components/social-media/components/social-post-content-multiposting';
import {PlatformPublishFields} from '@Components/social-media-wizard/components/platform-publish-fields/platform-publish-fields';
import {SocialMediaMediaTypeToggle} from '@Components/social-media-wizard/components/social-media-media-type-toggle/social-media-media-type-toggle';
import type {MediaTypeToggleOption} from '@Components/social-media-wizard/components/social-media-media-type-toggle/social-media-media-type-toggle.types';
import {MediaTypePreviewIconType} from '@Components/social-media-wizard/components/social-media-media-type-toggle/components/media-type-preview-icon/media-type-preview-icon.types';
import {SocialMediaPublishParamsStoryGuideline} from '@Components/social-media-wizard/components/social-media-publish-params-story-guideline';
import styles from './social-media-post-publish-params.module.scss';
import {useAppDispatch} from '@/hooks';

interface SocialMediaPostPublishParamsProps {
  account: SocialAccount;
  size?: SocialMediaWizardContentSize;
  platformMediaType?: PlatformMediaType;
}

export function SocialMediaPostPublishParams({
  account,
  size = SocialMediaWizardContentSize.XLARGE,
  platformMediaType = PlatformMediaType.POST,
}: SocialMediaPostPublishParamsProps): ReactElement | null {
  const publishingAction = useSocialPostPublishingAction();
  const publishingParams = usePublishingParams();
  const postPublishingParamsForAccount = usePostPublishingParamsForAccount(account.id);
  const reelPublishingParamsForAccount = useReelPublishingParamsForAccount(account.id);
  const storyPublishingParamsForAccount = useStoryPublishingParamsForAccount(account.id);
  const dispatch = useAppDispatch();
  const isVideoDesign = useIsPublishingVideoForAccount();
  const platformMediaTypeRef = useRef(platformMediaType);
  const isWeb = size === SocialMediaWizardContentSize.XLARGE || size === SocialMediaWizardContentSize.LARGE || size === SocialMediaWizardContentSize.MEDIUM;
  const [titleErrorMessage, setTitleErrorMessage] = useState<string | null>(null);
  const loadingState: LoadingStates = usePublishingStatusForAccountWithMediaType(account.id, platformMediaTypeRef.current);
  const accountResizeStatus = useAccountResizeCompletedStatusForPosters(account, platformMediaTypeRef.current);
  const wizardStep = useWizardStep();
  const previewParentRef = useRef<HTMLDivElement>(null);
  const animatingElementRef = useRef<HTMLDivElement>(null);
  const designsToPublishTo = useDesignsToPublish();
  const isAnyPosterResizingForForAnyMediaType = useIsAnyPosterResizingForAccount(account.id);
  const designsGraphicsMetaMapForAccount = useDesignsGraphicsMetaMapForAccountWithMediaType(account.id, platformMediaTypeRef.current);
  const hasAnyPosterResizeErroredOut = useHasAnyPosterResizeErroredForAccount(account.id, platformMediaTypeRef.current);
  const isAnyPosterLoading = useIsAnyPosterLoadingForAccount(account.id, platformMediaTypeRef.current);
  const doesAnyPosterNeedResize = useDoesAnyPosterRequireResizeForAccount(account.id, platformMediaTypeRef.current);
  const storyGuideline = useStoryGuidelineForAccount(account.id);
  const [isPlatformAnimationRunning, setIsPlatformAnimationRunning] = useState(false);

  const {isPreviewLoading, setIsPreviewLoading, triggerDefaultResizing, resizeLifecycle} = usePublishParamsResizePipeline({
    account,
    platformMediaTypeRef,
  });

  const {onClickResizeDesign} = usePublishParamsResizeActions({
    account,
    platformMediaTypeRef,
    resizeLifecycle,
  });

  const {onClickCover, getCoverUrlForSingleGraphicPost, isCoverCta, getTextForCoverCta} = usePublishParamsCoverFlow({
    account,
    platformMediaType,
    platformMediaTypeRef,
    isVideoDesign,
  });

  const runIncomingAnimation = (): void => {
    if (previewParentRef?.current) {
      previewParentRef.current.classList.remove(styles.opacityFade);
      previewParentRef.current.classList.add(styles.fade);
    }
  };

  const runOutgoingAnimation = (): void => {
    setTimeout((): void => {
      if (previewParentRef?.current) {
        previewParentRef.current.classList.add(styles.fadeBack);
      }
    }, 500);
  };

  const onCaptionTransitionEnd = (event: TransitionEvent): void => {
    if (event.target === previewParentRef.current) {
      if (previewParentRef.current.classList.contains(styles.fadeBack)) {
        previewParentRef.current.classList.remove(styles.fade);
        previewParentRef.current.classList.remove(styles.fadeBack);
        previewParentRef.current.classList.add(styles.opacityFade);
      }
    }
  };

  const onSocialPostCaptionEvent = (): void => {
    if (previewParentRef?.current) {
      runIncomingAnimation();
      runOutgoingAnimation();
    }
  };

  const onPlatformSelectionTransitionEvent = (event: Event): void => {
    const customEvent = event as CustomEvent;
    const detail = customEvent.detail as SocialPostPlatformSelectionTransitionEventDetail;

    const platformMediaTypeFromEvent = detail.platformSelection;
    const {accountId} = detail;

    if (account.id !== accountId) {
      return;
    }

    if (platformMediaTypeFromEvent === platformMediaTypeRef.current && animatingElementRef?.current) {
      animatingElementRef?.current?.classList.add(styles.fadeIn);
      setIsPlatformAnimationRunning(true);
    }
  };

  const onMediaTypeAnimationEnd = (): void => {
    if (!animatingElementRef?.current) {
      return;
    }

    animatingElementRef.current.classList.remove(styles.fadeIn);
    setIsPlatformAnimationRunning(false);
  };

  useEffect(() => {
    window.addEventListener(SocialPostCaptionOverwriteEvent, onSocialPostCaptionEvent);
    window.addEventListener(SocialPostPlatformSelectionTransitionEvent, onPlatformSelectionTransitionEvent);

    return (): void => {
      window.removeEventListener(SocialPostCaptionOverwriteEvent, onSocialPostCaptionEvent);
      window.removeEventListener(SocialPostPlatformSelectionTransitionEvent, onPlatformSelectionTransitionEvent);
    };
  }, [platformMediaType]);

  const setTitleError = (newValue: string | null): void => {
    setTitleErrorMessage(newValue);
  };

  const onReelToggle = (newReelToggleValue: boolean): void => {
    if (newReelToggleValue && storyPublishingParamsForAccount) {
      dispatch(setMediaTypeSelectionToReelAndStory(account.id));
      return;
    }

    if (newReelToggleValue) {
      dispatch(setMediaTypeSelectionToReel(account.id));
      return;
    }

    if (storyPublishingParamsForAccount) {
      dispatch(setMediaTypeSelectionToPostAndStory(account.id));
      return;
    }

    dispatch(setMediaTypeSelectionToPost(account.id));
  };

  const getFormElementsIfNeeded = (): ReactElement | null => {
    if (platformMediaType === PlatformMediaType.STORIES && publishingAction === undefined) {
      return null;
    }

    if (publishingAction === undefined) {
      return getFormElementsByGraphicType();
    }

    return <PublishingPost platformMediaType={platformMediaTypeRef.current} publishingAction={publishingAction} account={account} />;
  };

  const getAccountHeaderVisibilityClass = (): string => {
    return publishingAction === undefined ? '' : '_hidden';
  };

  const getFormElementsByGraphicType = (): ReactElement | null => {
    return (
      <PlatformPublishFields
        account={account}
        isVideoDesign={isVideoDesign}
        titleErrorMessage={titleErrorMessage}
        setTitleError={setTitleError}
        isAnyPosterResizingForForAnyMediaType={isAnyPosterResizingForForAnyMediaType}
        isReelMediaType={platformMediaTypeRef.current === PlatformMediaType.REELS}
        onReelToggle={onReelToggle}
      />
    );
  };

  const getSectionWithLeadingSpacer = (section: ReactElement | null): ReactElement | null => {
    if (section === null) {
      return null;
    }

    return (
      <>
        <FieldSpacer />
        {section}
      </>
    );
  };

  const getPreviewPublishScrim = (): ReactElement | null => {
    if (loadingState !== LoadingStates.LOADING || wizardStep !== SocialMediaWizardStep.PUBLISH) {
      return null;
    }

    return <div className={styles.previewPublishScrim} />;
  };

  const onLoadCallback = (): void => {
    setIsPreviewLoading(false);
  };

  const getSelectedMediaTypeToggleId = (): SocialMediaWizardMediaTypeRadioButtonIds => {
    const isReelOrPost = postPublishingParamsForAccount !== null || reelPublishingParamsForAccount !== null;

    if (storyPublishingParamsForAccount && isReelOrPost) {
      return SocialMediaWizardMediaTypeRadioButtonIds.BOTH;
    }

    if (storyPublishingParamsForAccount) {
      return SocialMediaWizardMediaTypeRadioButtonIds.STORY;
    }

    return SocialMediaWizardMediaTypeRadioButtonIds.POST;
  };

  const onSelectionChange = (selectedId: SocialMediaWizardMediaTypeRadioButtonIds): void => {
    if (selectedId === SocialMediaWizardMediaTypeRadioButtonIds.POST) {
      dispatch(setMediaTypeSelectionToPost(account.id));
    }

    if (selectedId === SocialMediaWizardMediaTypeRadioButtonIds.STORY) {
      dispatch(setMediaTypeSelectionToStory(account.id));
    }

    if (selectedId === SocialMediaWizardMediaTypeRadioButtonIds.BOTH) {
      if (reelPublishingParamsForAccount) {
        dispatch(setMediaTypeSelectionToReelAndStory(account.id));
        return;
      }

      if (postPublishingParamsForAccount || storyPublishingParamsForAccount) {
        dispatch(setMediaTypeSelectionToPostAndStory(account.id));
      }
    }
  };

  const shouldShowPlatformMediaTypeToggle = (): boolean => {
    if (publishingAction !== undefined) {
      return false;
    }

    const isMultiPosting = designsToPublishTo.length > 1;

    if (isMultiPosting || !doesAccountSupportMultipleMediaTypes(account)) {
      return false;
    }

    if (isFacebookAccount(account) && isVideoDesign) {
      return false;
    }

    return true;
  };

  const getPlatformMediaTypeToggle = (): ReactElement | null => {
    const mediaTypeOptions: MediaTypeToggleOption<SocialMediaWizardMediaTypeRadioButtonIds>[] = [
      {
        text: window.i18next.t('pmwjs_post'),
        id: SocialMediaWizardMediaTypeRadioButtonIds.POST,
        previewIconType: MediaTypePreviewIconType.POST,
      },
      {
        text: window.i18next.t('pmwjs_story'),
        id: SocialMediaWizardMediaTypeRadioButtonIds.STORY,
        previewIconType: MediaTypePreviewIconType.STORY,
      },
      {
        text: window.i18next.t('pmwjs_both'),
        id: SocialMediaWizardMediaTypeRadioButtonIds.BOTH,
        previewIconType: MediaTypePreviewIconType.BOTH,
      },
    ];

    if (!shouldShowPlatformMediaTypeToggle()) {
      return null;
    }

    return (
      <SocialMediaMediaTypeToggle
        isDisabled={isAnyPosterResizingForForAnyMediaType}
        disabledTooltipText={isAnyPosterResizingForForAnyMediaType ? window.i18next.t('pmwjs_your_design_is_being_resized') : ''}
        containerClassName={getMediaTypeToggleContainerClasses()}
        selectedId={getSelectedMediaTypeToggleId()}
        options={mediaTypeOptions}
        onSelect={onSelectionChange}
      />
    );
  };

  const getMediaTypeToggleContainerClasses = (): string => {
    return `${styles.transitionClasses} ${isPlatformAnimationRunning ? styles.fadeIn : ''}`;
  };

  const getStoryGuidelineIfNeeded = (): ReactElement | null => {
    if (publishingAction !== undefined) {
      return null;
    }

    if (platformMediaTypeRef.current === PlatformMediaType.STORIES) {
      return <SocialMediaPublishParamsStoryGuideline storyGuideline={storyGuideline} />;
    }

    return null;
  };

  const onRemoveAccountClick = useRemoveAccountFromPost(account);

  const getWebFormColumn = (): ReactElement => {
    return (
      <div className={`flex-v-row flex-content-start spacing-m-r-10 ${styles.contentContainer}`}>
        <SocialAccountLabel
          platformMediaType={platformMediaTypeRef.current}
          account={account}
          showAccountDetails
          subtitleSuffix={getAccountMediaTypeSubtitleSuffix(publishingParams, account, platformMediaTypeRef.current)}
          className={`${styles.accountHeader} ${getAccountHeaderVisibilityClass()}`}
          onRemoveAccountClick={onRemoveAccountClick}
        />
        {getSectionWithLeadingSpacer(getPlatformMediaTypeToggle())}
        {getSectionWithLeadingSpacer(getFormElementsIfNeeded())}
        {getSectionWithLeadingSpacer(getStoryGuidelineIfNeeded())}
      </div>
    );
  };

  const getPreviewCta = (): ReactElement => {
    return (
      <PublishParamsPreviewCta
        wizardStep={wizardStep}
        accountResizeStatus={accountResizeStatus}
        doesAnyPosterNeedResize={doesAnyPosterNeedResize}
        publishingAction={publishingAction}
        accountType={account.type}
        isAnyPosterLoading={isAnyPosterLoading}
        isPreviewLoading={isPreviewLoading}
        isCoverCta={isCoverCta()}
        coverCtaText={getTextForCoverCta()}
        onResize={onClickResizeDesign}
        onRetry={triggerDefaultResizing}
        onClickCover={onClickCover}
      />
    );
  };

  const getWebPreviewColumn = (): ReactElement => {
    return (
      <div ref={animatingElementRef} onAnimationEnd={onMediaTypeAnimationEnd} className={styles.previewContent}>
        <div
          onTransitionEnd={onCaptionTransitionEnd}
          ref={previewParentRef}
          className={`${styles.previewWrapper} ${!isPlatformAnimationRunning ? styles.previewWrapperColor : ''}`}
          key={getCoverUrlForSingleGraphicPost}
        >
          {getPreviewPublishScrim()}
          <SocialPostContent
            designsToPublish={designsToPublishTo}
            onLoadCallback={onLoadCallback}
            areDetailsLoading={false}
            parentContainerRef={previewParentRef}
            type={hasAnyPosterResizeErroredOut ? SocialPostContentType.MEDIA_RESIZING_FAILURE : SocialPostContentType.ACCOUNT_PREVIEW}
            hasSkeletonLoadingState
            account={account}
            accountsToPublishTo={[]}
            publishingParams={publishingParams}
            designsGraphicMetaMap={designsGraphicsMetaMapForAccount}
            platformMediaType={platformMediaTypeRef.current}
          />
          {getPreviewCta()}
        </div>
      </div>
    );
  };

  const getWebContent = (): ReactElement => {
    return (
      <div key={account.id} className="flex-h-row _full-width">
        {getWebFormColumn()}
        {getWebPreviewColumn()}
      </div>
    );
  };

  const getMobileContent = (): ReactElement => {
    return (
      <div className="flex-v-row _full-width">
        <SocialAccountLabel
          platformMediaType={platformMediaTypeRef.current}
          account={account}
          showAccountDetails
          subtitleSuffix={getAccountMediaTypeSubtitleSuffix(publishingParams, account, platformMediaTypeRef.current)}
          className={`${styles.accountHeader} ${getAccountHeaderVisibilityClass()}`}
          onRemoveAccountClick={onRemoveAccountClick}
        />
        {getSectionWithLeadingSpacer(getPlatformMediaTypeToggle())}
        {getSectionWithLeadingSpacer(getFormElementsIfNeeded())}
        {getSectionWithLeadingSpacer(getStoryGuidelineIfNeeded())}
      </div>
    );
  };

  return isWeb ? getWebContent() : getMobileContent();
}
